<template>
  <!-- 权限加载中时，不显示菜单 -->
  <el-menu v-if="!userStore.getIsPermissionLoading" class="el-menu-vertical-demo" :collapse="collapse" :default-active="activeMenuPath">
    <!-- Logo 和系统名称 -->
    <div class="flex items-center gap-3 px-4 py-3 menu-header" style="height: 60px;">
      <div class="flex items-center justify-center flex-shrink-0 rounded-lg w-9 h-9 bg-gradient-to-br from-blue-600 to-cyan-500">
        <span class="text-base font-bold text-white">HS</span>
      </div>
      <span v-if="!collapse" class="text-base font-bold text-gray-900 truncate">临床数据采集</span>
    </div>
    
    <template v-for="item in menuList" :key="item.path">
      <el-sub-menu :index="item.path" v-if="item.childList">
        <template #title>
          <el-icon :size="16" class="pr-2">
            <component :is="getIconComponent(item.icon)" />
          </el-icon>
          <span>{{ item.title }}</span>
        </template>
        <el-menu-item v-for="el in item.childList" :key="el.path" :index="el.path" @click="goto(el)">
          <el-icon :size="16" class="pr-2">
            <component :is="getIconComponent(el.icon)" />
          </el-icon>
          <span>{{ el.title }}</span>
        </el-menu-item>
      </el-sub-menu>
      <el-menu-item v-else @click="goto(item)" :index="item.path">
        <el-icon :size="16" class="pr-2">
          <component :is="getIconComponent(item.icon)" />
        </el-icon>
        <span>{{ item.title }}</span>
      </el-menu-item>
    </template>
  </el-menu>
</template>

<script lang="ts" setup>
import { ElMenu, ElSubMenu } from 'element-plus';
import { useRoute, useRouter } from 'vue-router';
import { useAppStore } from '@/store/modules/app';
import { useUserStore } from '@/store/modules/user';
import { computed, watch } from 'vue';
import { useTagsViewStore } from '@/store/modules/tagsView';
import { MenuList } from '@/router/modules';
import * as Icons from '@element-plus/icons-vue';

const router = useRouter();
const route = useRoute();
const appStore = useAppStore();
const userStore = useUserStore();
const tagStore = useTagsViewStore();
const collapse = computed(() => appStore.getCollapse);

// 计算当前激活的菜单路径
const activeMenuPath = computed(() => {
  // 获取当前路由
  const currentPath = route.path;
  
  // 查找匹配的菜单项
  const matchedMenu = MenuList.find((menu) => {
    // 检查一级菜单
    if (menu.path === currentPath) {
      return true;
    }
    // 检查二级菜单
    if (menu.childList?.some((child) => child.path === currentPath)) {
      return true;
    }
    return false;
  });
  
  if (matchedMenu) {
    // 如果是一级菜单，返回一级菜单路径
    if (matchedMenu.path === currentPath) {
      return matchedMenu.path;
    }
    // 如果是二级菜单，返回二级菜单路径
    const childItem = matchedMenu.childList?.find((child) => child.path === currentPath);
    if (childItem) {
      return childItem.path;
    }
  }
  
  // 默认返回当前路径
  return currentPath;
});

const getIconComponent = (iconName: string) => {
  // 将 icon-* 格式转换为驼峰命名法，然后从 Icons 中获取

  return (Icons as any)[iconName] || Icons.DocumentCopy;
};

/**
 * 递归过滤菜单：根据权限过滤
 */
const filterMenuByPermission = (menus: any[]): any[] => {
  if (userStore.permissions.length === 0) {
    return menus;
  }

  return menus
    .map((menu) => {
      const menuCopy = { ...menu };

      // 递归过滤子菜单
      if (Array.isArray(menuCopy.childList) && menuCopy.childList.length > 0) {
        const filteredChildren = filterMenuByPermission(menuCopy.childList);
        menuCopy.childList = filteredChildren;
      }

      // 检查是否有权限：如果没有设置 requiresPermission 或设置为 false，则默认允许
      const hasPermission = userStore.hasPermission(menu.name);

      // 如果有权限或有可见的子菜单，则保留此菜单
      if (hasPermission || (menuCopy.childList && menuCopy.childList.length > 0)) {
        return menuCopy;
      }

      return null;
    })
    .filter((menu) => menu !== null);
};

const menuList = computed(() => {
  const allMenus = MenuList.map((el) => {
    return {
      ...el,
      childList: el.childList?.filter((item) => !item.noMenu),
    };
  });

  // 根据权限过滤菜单
  const filtered = filterMenuByPermission(allMenus);

  return filtered;
});

const goto = (item: any) => {
  appStore.setMobileMenuDrawer(false);
  tagStore.addView(item);
  router.push(item.path);
};
</script>

<style scoped>
:deep(.el-menu-item svg),
:deep(.el-sub-menu__title svg) {}

.menu-header {
  border-bottom: 1px solid #e5e7eb;
  background-color: #fafafa;
  transition: all 0.3s ease;
}

:deep(.el-menu--vertical.is-collapse) .menu-header {
  padding: 12px 4px !important;
}

:deep(.el-menu--vertical:not(.is-collapse)) .menu-header {
  padding: 12px 16px !important;
}
</style>
